import { useEffect, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { LoadedProps, RouteLoadProps, defRoute, useRouter } from '@components/router';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { CampaignPageShell } from '@course/components/campaign/campaign-page-shell';
import { CampaignHero } from '@course/components/campaign/campaign-hero';
import { useCurrentUser } from 'client/lib/auth';
import { showError } from '@components/app-error';
import { BtnPrimary } from '@components/buttons';
import { ERROR_CODES } from 'shared/consts';

export const route = defRoute({
  isPublic: true,
  load,
  Page,
});

async function load(route: RouteLoadProps) {
  return rpx.courseCheckout.getEnrollmentPage({
    priceId: route.params.priceId,
  });
}

type Props = LoadedProps<typeof load>;
type LoadedState = RpxResponse<typeof rpx.courseCheckout.getEnrollmentPage>;

function Page(props: Props) {
  const { course, config, price, alreadyEnrolled } = props.state;
  useDocumentTitle([config.hero.headline, course.title]);

  return (
    <CampaignPageShell>
      <CampaignHero
        headline={config.hero.headline}
        subtitle={config.hero.subtitle}
        logoUrl={config.hero.logoUrl}
      />
      <main class="enrollment-page">
        <EnrollmentCard
          priceId={price.id}
          courseId={course.id}
          config={config}
          alreadyEnrolled={alreadyEnrolled}
        />
      </main>
    </CampaignPageShell>
  );
}

type EnrollmentCardProps = {
  priceId: string;
  courseId: string;
  config: LoadedState['config'];
  alreadyEnrolled: boolean;
};

type View = 'idle' | 'submitting' | 'success';

function EnrollmentCard({ priceId, courseId, config, alreadyEnrolled }: EnrollmentCardProps) {
  const router = useRouter();
  const user = useCurrentUser();
  const [view, setView] = useState<View>(alreadyEnrolled ? 'success' : 'idle');
  const [enrolledViaForm, setEnrolledViaForm] = useState(false);
  // Enrolled an existing account by email without signing in: the owner has
  // been emailed a sign-in link, and this browser is not signed in.
  const [passEmail, setPassEmail] = useState<string | undefined>(undefined);

  if (alreadyEnrolled || view === 'success') {
    return (
      <SuccessState
        courseId={courseId}
        config={config}
        autoRedirect={enrolledViaForm && !passEmail}
        alreadyEnrolledOnLoad={alreadyEnrolled && !enrolledViaForm}
        passEmail={passEmail}
      />
    );
  }

  if (user) {
    return (
      <LoggedInCard
        priceId={priceId}
        config={config}
        userName={user.name}
        view={view}
        setView={setView}
        onSuccess={() => {
          setEnrolledViaForm(true);
          setView('success');
        }}
      />
    );
  }

  return (
    <GuestCard
      priceId={priceId}
      config={config}
      view={view}
      setView={setView}
      onSuccess={(opts) => {
        setEnrolledViaForm(true);
        setPassEmail(opts?.passEmail);
        setView('success');
      }}
      onExistingGuide={() => {
        // The user has a guide-level account on the same email and can't enroll
        // here. Bounce them to the login page so they can sign in with a
        // different account if they have one.
        router.goto('/login');
      }}
    />
  );
}

type LoggedInCardProps = {
  priceId: string;
  config: LoadedState['config'];
  userName: string;
  view: View;
  setView: (v: View) => void;
  onSuccess: () => void;
};

function LoggedInCard({ priceId, config, userName, view, setView, onSuccess }: LoggedInCardProps) {
  const ctaText = config.ctaText || 'Get instant access';
  const firstName = (userName || '').trim().split(/\s+/)[0];
  const greeting = firstName ? `Hi ${firstName} — ready to dive in?` : 'Ready to dive in?';

  async function handleEnroll() {
    setView('submitting');
    try {
      await rpx.courseCheckout.enrollFree({ priceId });
      onSuccess();
    } catch (err) {
      setView('idle');
      showError(err);
    }
  }

  return (
    <div class="enrollment-card">
      <p class="enrollment-card__greeting">{greeting}</p>
      {config.bodyHtml && (
        <div class="enrollment-card__body" dangerouslySetInnerHTML={{ __html: config.bodyHtml }} />
      )}
      <BtnPrimary
        class="enrollment-card__cta"
        isLoading={view === 'submitting'}
        onClick={handleEnroll}
      >
        {ctaText}
      </BtnPrimary>
    </div>
  );
}

type GuestCardProps = {
  priceId: string;
  config: LoadedState['config'];
  view: View;
  setView: (v: View) => void;
  onSuccess: (opts?: { passEmail?: string }) => void;
  onExistingGuide: () => void;
};

function GuestCard({ priceId, config, view, setView, onSuccess, onExistingGuide }: GuestCardProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const ctaText = config.ctaText || 'Get instant access';

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      return;
    }
    setView('submitting');
    try {
      const result = await rpx.courseCheckout.enrollFree({
        priceId,
        name: name.trim(),
        email: email.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      onSuccess(result.checkoutPass ? { passEmail: email.trim() } : undefined);
    } catch (err: any) {
      setView('idle');
      if (err?.data?.code === ERROR_CODES.userExists) {
        onExistingGuide();
        return;
      }
      showError(err);
    }
  }

  return (
    <div class="enrollment-card">
      <h2 class="enrollment-card__title">Get free access</h2>
      <p class="enrollment-card__subtitle">Enter your details and we'll set you up.</p>
      {config.bodyHtml && (
        <div class="enrollment-card__body" dangerouslySetInnerHTML={{ __html: config.bodyHtml }} />
      )}
      <form class="enrollment-form" onSubmit={handleSubmit}>
        <label class="enrollment-form__field">
          <span class="enrollment-form__label">Your name</span>
          <input
            type="text"
            class="enrollment-form__input"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            required
            disabled={view === 'submitting'}
            autoComplete="name"
          />
        </label>
        <label class="enrollment-form__field">
          <span class="enrollment-form__label">Email address</span>
          <input
            type="email"
            class="enrollment-form__input"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            required
            disabled={view === 'submitting'}
            autoComplete="email"
          />
        </label>
        <BtnPrimary class="enrollment-card__cta" isLoading={view === 'submitting'}>
          {ctaText}
        </BtnPrimary>
        <p class="enrollment-form__hint">We'll email you a sign-in link.</p>
      </form>
    </div>
  );
}

type SuccessStateProps = {
  courseId: string;
  config: LoadedState['config'];
  autoRedirect: boolean;
  alreadyEnrolledOnLoad: boolean;
  /** Set when an existing account was enrolled by email without signing in. */
  passEmail?: string;
};

function SuccessState({
  courseId,
  config,
  autoRedirect,
  alreadyEnrolledOnLoad,
  passEmail,
}: SuccessStateProps) {
  // Not signed in: the course link would only bounce to the login page, so
  // say so, and send them there on purpose.
  const courseUrl = passEmail
    ? `/login?redirect=${encodeURIComponent(`/courses/${courseId}`)}`
    : `/courses/${courseId}`;
  const headline = alreadyEnrolledOnLoad
    ? config.alreadyEnrolledMessage || "You're already enrolled"
    : config.successHeadline || "You're in!";
  const ctaText = config.successCtaText || 'Go to the course';

  useEffect(() => {
    if (!autoRedirect) {
      return;
    }
    const t = window.setTimeout(() => {
      window.location.href = courseUrl;
    }, 3500);
    return () => window.clearTimeout(t);
  }, [autoRedirect, courseUrl]);

  return (
    <div class="enrollment-card enrollment-card--success">
      <div class="enrollment-success__check" aria-hidden="true">
        <svg viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
          <circle cx="26" cy="26" r="24" fill="none" stroke="currentColor" stroke-width="2.5" />
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="3.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M14 27 l9 9 l16 -18"
          />
        </svg>
      </div>
      <h2 class="enrollment-card__title">{headline}</h2>
      {passEmail && (
        <p class="enrollment-card__subtitle">
          It's been added to the account for <strong>{passEmail}</strong>. We've emailed a sign-in
          link to that address.
        </p>
      )}
      {config.successBodyHtml && (
        <div
          class="enrollment-card__body"
          dangerouslySetInnerHTML={{ __html: config.successBodyHtml }}
        />
      )}
      <BtnPrimary class="enrollment-card__cta" onClick={() => (window.location.href = courseUrl)}>
        {passEmail ? 'Sign in' : ctaText}
      </BtnPrimary>
    </div>
  );
}
